import { CustomerData, Reservation, ReservationSource } from '@backend/domain'
import { PropertyUpdateMap, ReservationState } from '@core/types'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { Transform, Type } from 'class-transformer'
import {
	ArrayMaxSize,
	IsArray,
	IsDate,
	IsEmail,
	IsNotEmpty,
	IsNotEmptyObject,
	IsOptional,
	IsString,
	IsUUID,
	Length,
	Min,
	ValidateNested,
} from 'class-validator'
import { WidgetSettingsResponse } from '../../settings'

export class ReservationResponse {
	@ApiProperty({ type: String })
	id: string

	@ApiProperty({ type: Number })
	noOfPersons: number

	@ApiProperty({ type: Date })
	startDate: Date

	@ApiProperty({ type: Date })
	endDate: Date

	@ApiProperty({ enum: ReservationState })
	state: ReservationState

	@ApiProperty({ type: CustomerData })
	customerData: CustomerData

	@ApiProperty({ enum: ReservationSource })
	source: ReservationSource

	@ApiPropertyOptional({ type: String })
	comment?: string

	@ApiProperty({ type: String, isArray: true })
	tableIds: string[]

	@ApiPropertyOptional({ type: String, format: 'uuid' })
	tableCombinationId?: string

	@ApiProperty({ type: Date })
	createdAt: Date
}

export class CreateReservationRequest {
	@ApiProperty({ type: Number })
	@IsNotEmpty()
	@Min(1)
	noOfPersons: number

	@ApiProperty({ type: Date })
	@IsNotEmpty()
	@Transform(({ value }) => new Date(value))
	@IsDate()
	startDate: Date

	@ApiProperty({ type: Date })
	@IsNotEmpty()
	@Transform(({ value }) => new Date(value))
	@IsDate()
	endDate: Date

	@ApiProperty({ type: CustomerData })
	@IsNotEmptyObject()
	customerData: CustomerData

	@ApiPropertyOptional({ type: String })
	@IsOptional()
	@IsString()
	comment?: string

	/** Additional guest emails that receive the reservation notifications (no management link). */
	@ApiPropertyOptional({ type: String, isArray: true })
	@IsOptional()
	@IsArray()
	@ArrayMaxSize(5)
	@IsEmail({}, { each: true })
	companionEmails?: string[]

	@ApiPropertyOptional({ type: String, format: 'uuid' })
	@IsOptional()
	@IsUUID()
	tableId?: string

	@ApiPropertyOptional({ type: String, format: 'uuid' })
	@IsOptional()
	@IsUUID()
	tableCombinationId?: string
}

export class UpdateReservationRequest extends CreateReservationRequest {
	@ApiProperty({ type: String, format: 'uuid' })
	@IsNotEmpty()
	@IsUUID()
	id: string
}

export class VerifyReservationResponse {
	@ApiProperty({ type: String, format: 'uuid' })
	reservationId: string

	@ApiProperty({ type: Boolean })
	success: boolean
}

export class WidgetCreateReservationResponse {
	@ApiProperty({ type: Boolean })
	confirmed: boolean

	@ApiPropertyOptional({ type: String })
	reservationId?: string

	@ApiPropertyOptional({ enum: ReservationState })
	state?: ReservationState

	/** Absolute URL the guest can use to view/update/cancel this reservation without an account. */
	@ApiPropertyOptional({ type: String })
	managementUrl?: string
}

/** Customer data a guest may edit through the self-service management link. */
export class ManagedCustomerDataRequest {
	@ApiPropertyOptional({ type: String })
	@IsOptional()
	@IsString()
	@Length(1, 120)
	firstName?: string

	@ApiProperty({ type: String })
	@IsNotEmpty()
	@IsString()
	@Length(1, 120)
	lastName: string

	@ApiPropertyOptional({ type: String, format: 'email' })
	@IsOptional()
	@IsEmail()
	email?: string

	@ApiPropertyOptional({ type: String })
	@IsOptional()
	@IsString()
	phoneNumber?: string
}

export class UpdateManagedReservationRequest {
	@ApiProperty({ type: Number })
	@IsNotEmpty()
	@Min(1)
	noOfPersons: number

	@ApiProperty({ type: Date })
	@IsNotEmpty()
	@Transform(({ value }) => new Date(value))
	@IsDate()
	startDate: Date

	@ApiProperty({ type: Date })
	@IsNotEmpty()
	@Transform(({ value }) => new Date(value))
	@IsDate()
	endDate: Date

	@ApiProperty({ type: ManagedCustomerDataRequest })
	@IsNotEmptyObject()
	@ValidateNested()
	@Type(() => ManagedCustomerDataRequest)
	customerData: ManagedCustomerDataRequest

	@ApiPropertyOptional({ type: String })
	@IsOptional()
	@IsString()
	comment?: string

	/** Additional guest emails that receive the reservation notifications (no management link). */
	@ApiPropertyOptional({ type: String, isArray: true })
	@IsOptional()
	@IsArray()
	@ArrayMaxSize(5)
	@IsEmail({}, { each: true })
	companionEmails?: string[]
}

export enum ManagedReservationLockReason {
	cancelled = 'cancelled',
	inPast = 'inPast',
	notModifiable = 'notModifiable',
}

export class ManagedReservationDetails {
	@ApiProperty({ type: Number })
	noOfPersons: number

	@ApiProperty({ type: Date })
	startDate: Date

	@ApiProperty({ type: Date })
	endDate: Date

	@ApiProperty({ enum: ReservationState })
	state: ReservationState

	@ApiPropertyOptional({ type: String })
	comment?: string

	@ApiProperty({ type: String })
	name: string

	@ApiPropertyOptional({ type: String })
	firstName?: string

	@ApiPropertyOptional({ type: String })
	lastName?: string

	@ApiPropertyOptional({ type: String })
	email?: string

	@ApiPropertyOptional({ type: String })
	phoneNumber?: string

	@ApiPropertyOptional({ type: String, isArray: true })
	companionEmails?: string[]
}

export class ManagedReservationResponse {
	@ApiProperty({ type: ManagedReservationDetails })
	reservation: ManagedReservationDetails

	@ApiProperty({ type: WidgetSettingsResponse })
	settings: WidgetSettingsResponse

	/** Whether the guest may still change or cancel this reservation. */
	@ApiProperty({ type: Boolean })
	canModify: boolean

	@ApiPropertyOptional({ enum: ManagedReservationLockReason })
	lockReason?: ManagedReservationLockReason
}

export type ReservationUpdates = Array<PropertyUpdateMap<Reservation>>

/** Semantic reservation lifecycle events used to pick email templates and subjects. */
export enum ReservationEmailEvent {
	/** New reservation, auto-approved */
	createdConfirmed = 'createdConfirmed',
	/** New reservation, waiting for restaurant approval */
	createdPending = 'createdPending',
	/** Existing reservation approved by the restaurant */
	confirmed = 'confirmed',
	/** Existing reservation moved back to unconfirmed */
	pending = 'pending',
	/** Date/time/persons/contact data changed */
	updated = 'updated',
	/** Cancelled by the guest via the self-service link */
	cancelledByGuest = 'cancelledByGuest',
	/** Cancelled/rejected by the restaurant */
	cancelledByRestaurant = 'cancelledByRestaurant',
}

/** Aggregated state of a restaurant day, shown in owner notification emails. */
export class ReservationDayStats {
	@ApiProperty({ type: Number })
	reservationCount: number

	@ApiProperty({ type: Number })
	guestCount: number
}
